import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { ArrowLeft, Printer } from 'lucide-react';

function ViewMessage({ user }) {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [senderInfo, setSenderInfo] = useState(null);

    useEffect(() => {
        fetchMessage();
    }, [orderId]);

    const fetchMessage = async () => {
        try {
            setLoading(true);
            const messageDoc = await getDoc(doc(db, 'workOrders', orderId));

            if (messageDoc.exists()) {
                const messageData = { id: messageDoc.id, ...messageDoc.data() };
                setMessage(messageData);

                if (user?.uid && !messageData.readByReceiver) {
                    const targetId = user.companyId || user.uid;
                    // 본인이 수신자인 경우에만 읽음 처리
                    if (messageData.toUserId === targetId) {
                        await updateDoc(doc(db, 'workOrders', orderId), {
                            readByReceiver: true,
                            readAt: new Date()
                        });
                    }
                }

                // 발신자 정보 조회
                const senderDoc = await getDoc(doc(db, 'users', messageData.fromUserId));
                if (senderDoc.exists()) setSenderInfo(senderDoc.data());
            }
        } catch (error) {
            console.error('메시지 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatShortDate = (timestamp, withWeekday = false) => {
        if (!timestamp) return '-';
        let date = timestamp.toDate ? timestamp.toDate() : timestamp instanceof Date ? timestamp : new Date(timestamp);
        if (isNaN(date.getTime())) return '-';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const weekdays = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
        return withWeekday
            ? `${year}.${month}.${day} ${weekdays[date.getDay()]}`
            : `${year}.${month}.${day}`;
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    if (!message) {
        return (
            <div style={styles.error}>
                <p>메시지를 찾을 수 없습니다.</p>
                <button onClick={() => navigate('/orders')} style={styles.backButton}>
                    목록으로
                </button>
            </div>
        );
    }

    const senderName = message.clinicName || senderInfo?.businessName ||
        senderInfo?.companyName || senderInfo?.name || '알 수 없음';

    return (
        <>
            <style>
                {`@media print { 
                    html, body { 
                        margin: 0 !important; 
                        padding: 0 !important; 
                        background: white !important; 
                    } 
                    body * { visibility: hidden; } 
                    .print-area, .print-area * { visibility: visible; } 
                    .print-area { 
                        position: absolute; 
                        left: 0; 
                        top: 0; 
                        width: 190mm !important; 
                        padding: 10mm !important; 
                        box-shadow: none !important; 
                    } 
                    .no-print { display: none !important; } 
                }`}
            </style>

            <div style={styles.container}>
                <div style={styles.actionBar} className="no-print">
                    <button onClick={() => navigate('/orders')} style={styles.backBtn}>
                        <ArrowLeft size={18} /> 목록으로
                    </button>
                    <button onClick={() => window.print()} style={styles.printBtn}>
                        <Printer size={18} /> 인쇄하기
                    </button>
                </div>

                <div style={styles.messageContainer} className="print-area">
                    {/* 메시지 헤더 */}
                    <div style={styles.header}>
                        <div style={styles.icon}>💬</div>
                        <div style={styles.headerContent}>
                            <div style={styles.badge}>이메일 메시지</div>
                            <h1 style={styles.title}>
                                {message.emailSubject || message.emailInfo?.emailSubject || '제목 없음'}
                            </h1>
                        </div>
                    </div>

                    {/* 메시지 정보 */}
                    <div style={styles.infoBox}>
                        <div style={styles.infoItem}>
                            <div style={styles.label}>발신</div>
                            <div style={styles.value}>{senderName}</div>
                            <div style={styles.subValue}>
                                {message.sourceEmail || message.emailInfo?.sourceEmail}
                            </div>
                        </div>
                        <div style={styles.infoItem}>
                            <div style={styles.label}>수신일</div>
                            <div style={styles.value}>
                                {formatShortDate(
                                    message.emailReceivedAt ||
                                    message.emailInfo?.emailReceivedAt ||
                                    message.createdAt,
                                    true
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 메시지 내용 */}
                    <div style={styles.contentBox}>
                        <div style={styles.contentLabel}>메시지 내용</div>
                        <div style={styles.contentText}>
                            {(message.emailSubject || message.emailInfo?.emailSubject) &&
                                `제목: ${message.emailSubject || message.emailInfo?.emailSubject}\n\n`}
                            {message.messageContent || '내용이 없습니다.'}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f1f5f9',
        padding: '20px'
    },
    actionBar: {
        maxWidth: '900px',
        margin: '0 auto 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 14px',
        backgroundColor: '#8b5cf6',
        borderRadius: '10px'
    },
    backBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: 'rgba(255,255,255,0.2)',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    printBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#fff',
        color: '#8b5cf6',
        border: 'none',
        borderRadius: '6px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    messageContainer: {
        maxWidth: '900px',
        margin: '0 auto',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '40px',
        minHeight: '400px'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e2e8f0'
    },
    icon: {
        width: '60px',
        height: '60px',
        backgroundColor: '#f3e8ff',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '32px',
        flexShrink: 0
    },
    headerContent: {
        flex: 1
    },
    badge: {
        display: 'inline-block',
        padding: '4px 12px',
        backgroundColor: '#f3e8ff',
        color: '#6b21a8',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '600',
        marginBottom: '8px'
    },
    title: {
        margin: 0,
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b'
    },
    infoBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '24px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px'
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px'
    },
    label: {
        fontSize: '13px',
        color: '#64748b',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    value: {
        fontSize: '18px',
        color: '#1e293b',
        fontWeight: '600'
    },
    subValue: {
        fontSize: '14px',
        color: '#94a3b8'
    },
    contentBox: {
        marginTop: '24px'
    },
    contentLabel: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '12px'
    },
    contentText: {
        padding: '24px',
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: '8px',
        fontSize: '15px',
        lineHeight: '1.8',
        color: '#1e293b',
        whiteSpace: 'pre-wrap',
        minHeight: '200px'
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#64748b'
    },
    spinner: {
        width: '36px',
        height: '36px',
        border: '3px solid #e5e7eb',
        borderTop: '3px solid #8b5cf6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    error: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#ef4444'
    },
    backButton: {
        marginTop: '16px',
        padding: '8px 16px',
        backgroundColor: '#8b5cf6',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer'
    }
};

export default ViewMessage;